import { useState } from 'react';
import { Send, X, CheckCircle, Sparkles } from 'lucide-react';
import contactController from '../controllers/contactController'; // Import controller

const ContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (error) setError(null);
  };

  const createConfetti = () => {
    const confettiCount = 50;
    const confetti = [];
    const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#f9ca24', '#6c5ce7', '#a29bfe'];

    for (let i = 0; i < confettiCount; i++) {
      confetti.push({
        id: i,
        left: Math.random() * 100,
        animationDelay: Math.random() * 3,
        backgroundColor: colors[Math.floor(Math.random() * colors.length)],
      });
    }
    return confetti;
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    // Call contactController.createPublicInquiry
    const result = await contactController.createPublicInquiry(formData);

    if (result.success) {
      console.log('Contact inquiry submitted:', result.data);
      setSuccess(true);
      setShowConfetti(true);

      setTimeout(() => {
        setShowConfetti(false);
      }, 4000);

      setTimeout(() => {
        setFormData({ firstName: '', lastName: '', email: '', phone: '', subject: '', message: '' });
        setSuccess(false);
        onClose();
      }, 3500);
    } else {
      // Handle errors
      if (result.errors) {
        setError(Array.isArray(result.errors) ? result.errors.join(', ') : result.errors);
      } else {
        setError(result.message || 'Failed to send message. Please try again.');
      }
    }

    setLoading(false);
  };

  if (!isOpen) return null;

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
    },
    modal: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      maxWidth: '500px',
      width: '100%',
      maxHeight: '90vh',
      overflow: 'auto',
      position: 'relative',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
    },
    header: {
      padding: '24px 24px 16px',
      borderBottom: '1px solid #e5e7eb',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    title: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#1f2937',
    },
    closeButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '4px',
      color: '#6b7280',
      transition: 'color 0.3s',
    },
    content: {
      padding: '24px',
    },
    formContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      textAlign: 'left',
    },
    inputRow: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '16px',
    },
    label: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151',
      textAlign: 'left',
    },
    input: {
      padding: '12px 16px',
      border: '1.5px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '15px',
      outline: 'none',
      transition: 'border-color 0.3s',
    },
    textarea: {
      padding: '12px 16px',
      border: '1.5px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '15px',
      outline: 'none',
      minHeight: '120px',
      resize: 'vertical',
      fontFamily: 'inherit',
      transition: 'border-color 0.3s',
    },
    button: {
      backgroundColor: '#2563eb',
      color: '#ffffff',
      padding: '14px 24px',
      borderRadius: '8px',
      border: 'none',
      fontWeight: '600',
      fontSize: '16px',
      cursor: 'pointer',
      transition: 'background-color 0.3s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      marginTop: '10px',
    },
    buttonDisabled: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed',
    },
    errorMessage: {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
      padding: '16px',
      borderRadius: '8px',
      textAlign: 'center',
      fontWeight: '500',
      marginBottom: '20px',
    },
    successDialog: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      animation: 'fadeIn 0.3s ease-in',
    },
    successCard: {
      backgroundColor: '#ffffff',
      borderRadius: '20px',
      padding: '48px 40px',
      maxWidth: '450px',
      width: '90%',
      textAlign: 'center',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      position: 'relative',
      overflow: 'hidden',
      animation: 'scaleIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    },
    successIcon: {
      width: '80px',
      height: '80px',
      backgroundColor: '#10b981',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 24px',
      animation: 'bounceIn 0.6s ease-out 0.2s both',
    },
    successTitle: {
      fontSize: '32px',
      fontWeight: '700',
      color: '#1f2937',
      marginBottom: '16px',
      animation: 'slideUp 0.5s ease-out 0.3s both',
    },
    successMessage: {
      fontSize: '18px',
      color: '#6b7280',
      lineHeight: '1.6',
      marginBottom: '8px',
      animation: 'slideUp 0.5s ease-out 0.4s both',
    },
    successSubMessage: {
      fontSize: '16px',
      color: '#9ca3af',
      marginBottom: '32px',
      animation: 'slideUp 0.5s ease-out 0.5s both',
    },
    sparkleIcon: {
      display: 'inline-block',
      animation: 'rotate 2s linear infinite',
    },
    confettiPiece: {
      position: 'absolute',
      width: '10px',
      height: '10px',
      top: '-10px',
      opacity: 0,
      animation: 'confettiFall 3s ease-out forwards',
    },
  };

  const keyframesStyle = `
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes scaleIn {
      from { transform: scale(0.5); opacity: 0; }
      to { transform: scale(1); opacity: 1; }
    }
    @keyframes bounceIn {
      0% { transform: scale(0); }
      50% { transform: scale(1.1); }
      100% { transform: scale(1); }
    }
    @keyframes slideUp {
      from { transform: translateY(20px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
    @keyframes rotate {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    @keyframes confettiFall {
      0% {
        top: -10px;
        opacity: 1;
      }
      100% {
        top: 100vh;
        opacity: 0;
        transform: translateX(${Math.random() * 100 - 50}px) rotate(${Math.random() * 360}deg);
      }
    }
  `;

  return (
    <>
      <style>{keyframesStyle}</style>
      
      <div style={styles.overlay} onClick={onClose}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div style={styles.header}>
            <h2 style={styles.title}>Contact Us</h2>
            <button
              onClick={onClose}
              style={styles.closeButton}
              onMouseEnter={(e) => e.target.style.color = '#1f2937'}
              onMouseLeave={(e) => e.target.style.color = '#6b7280'}
            >
              <X size={24} />
            </button>
          </div>

          <div style={styles.content}>
            {error && (
              <div style={styles.errorMessage}>
                ✕ {error}
              </div>
            )}

            <div style={styles.formContainer}>
              <div style={styles.inputRow}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>First Name *</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={formData.firstName}
                    onChange={(e) => handleChange('firstName', e.target.value)}
                    placeholder="John"
                    disabled={loading}
                    onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  />
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Last Name *</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={formData.lastName}
                    onChange={(e) => handleChange('lastName', e.target.value)}
                    placeholder="Doe"
                    disabled={loading}
                    onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  />
                </div>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Email Address *</label>
                <input
                  type="email"
                  style={styles.input}
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="john@example.com"
                  disabled={loading}
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Phone Number *</label>
                <input
                  type="tel"
                  style={styles.input}
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="+1 (555) 000-0000"
                  disabled={loading}
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Subject</label>
                <input
                  type="text"
                  style={styles.input}
                  value={formData.subject}
                  onChange={(e) => handleChange('subject', e.target.value)}
                  placeholder="How can we help?"
                  disabled={loading}
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Message</label>
                <textarea
                  style={styles.textarea}
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  placeholder="Tell us more about your project..."
                  disabled={loading}
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
              </div>

              <button
                onClick={handleSubmit}
                style={{
                  ...styles.button,
                  ...(loading ? styles.buttonDisabled : {})
                }}
                disabled={loading}
                onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = '#1d4ed8')}
                onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = '#2563eb')}
              >
                {loading ? 'Sending...' : (
                  <>
                    <Send size={18} />
                    Send Message
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Success Dialog with Confetti */}
      {success && (
        <div style={styles.successDialog}>
          <div style={styles.successCard}>
            {showConfetti && createConfetti().map((conf) => (
              <div
                key={conf.id}
                style={{
                  ...styles.confettiPiece,
                  left: `${conf.left}%`,
                  backgroundColor: conf.backgroundColor,
                  animationDelay: `${conf.animationDelay}s`,
                }}
              />
            ))}

            <div style={styles.successIcon}>
              <CheckCircle size={48} color="#ffffff" strokeWidth={3} />
            </div>

            <h2 style={styles.successTitle}>
              🎉 Success! 🎉
            </h2>
            <p style={styles.successMessage}>
              Thank you for reaching out to us!
            </p>
            <p style={styles.successSubMessage}>
              We've received your message and will get back to you within 24 hours.
              <br />
              <span style={styles.sparkleIcon}>
                <Sparkles size={20} color="#f59e0b" />
              </span>
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ContactModal;